import { Component, OnInit, signal, computed, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MessageService, MenuItem, ConfirmationService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { RegistryTableComponent } from '../../../../core/registry-table/registry-table.component';
import { ColumnConfig } from '../../dashboard/table-config';
import { 
  ContractAction, 
  ContractActionType,
  ContractActionStatus,
  CONTRACT_ACTION_TYPE_LABELS, 
  CONTRACT_ACTION_STATUS_LABELS,
  CONTRACT_ACTION_TYPE_OPTIONS 
} from '../../../models/contract-action.model';
import { ContractInvolvedEntity } from '../../../models/contract.model';
import { ContractActionService, CreateActionRequest, ApiResponse } from '../../../services/contract-action.service';
import { FormDropdownService, FormDropdownItem } from '../../../../core/services/form-dropdown.service';
import { AxaEntity, AxaEntityService } from '../../../../core/services/axa-entity.service';
import { ContractRegistryService } from '../../../services/contract-registry.service';
import { DocumentTemplateV2Service } from '../../../services/document-template-v2.service';
import { DocumentAssetSummary } from '../../../models/document-asset.model';
import { isSelectableTemplate, toTemplateSelectionRow } from '../../../models/document-asset-labels';
import { NavigationContextService } from '../../../../core/services/navigation-context.service';
import { NoSpecialCharsValidator, escapeHtml } from '../../../../core/validators/no-special-chars.validator';

@Component({
  selector: 'app-contract-action-form',
  standalone: true,
  providers: [MessageService, ConfirmationService],
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    InputNumber,
    Textarea,
    Toast,
    MessageModule,
    TagModule,
    ProgressSpinnerModule,
    DialogModule,
    ConfirmDialogModule,
    RegistryTableComponent
  ],
  templateUrl: './contract-action-form.component.html',
  styleUrl: './contract-action-form.component.scss'
})
export class ContractActionFormComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  private messageService = inject(MessageService);
  private confirmDialogService = inject(ConfirmationService);
  private contractActionService = inject(ContractActionService);
  private contractRegistryService = inject(ContractRegistryService);
  private formDropdownService = inject(FormDropdownService);
  private axaEntityService = inject(AxaEntityService);
  private documentTemplateV2Service = inject(DocumentTemplateV2Service);
  private navigationContext = inject(NavigationContextService);

  // Route params
  contractId = signal<string>(''); // Can be numeric ID or alphanumeric code like CNTR-000000001
  contractNumericId = signal<number | null>(null); // Numeric ID from API response
  actionId = signal<number | null>(null);
  
  // UI State
  isLoading = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([]);
  
  // Contract info
  contractName = signal<string>('');
  contractCode = signal<string>('');
  
  // Contract current values (for pre-population and validation)
  currentEndDate = signal<Date | null>(null);
  currentObject = signal<string>('');
  currentFees = signal<number | null>(null);
  currentEntities = signal<ContractInvolvedEntity[]>([]);
  
  // Action data
  action = signal<ContractAction | null>(null);
  actionType = signal<ContractActionType | null>(null);
  actionName = signal<string>('');
  actionNotes = signal<string>('');
  
  // Type-specific fields
  newEndDate = signal<Date | undefined>(undefined);
  newObject = signal<string>('');
  newCurrency = signal<string | undefined>(undefined);
  newTotalContractValue = signal<number | undefined>(undefined);
  newAnnualValue = signal<number | undefined>(undefined);
  newEntities = signal<ContractInvolvedEntity[]>([]);
  terminationReason = signal<string>('');
  renewalNotes = signal<string>('');
  selectedTemplateId = signal<string | undefined>(undefined);
  
  // Dropdown options
  actionTypeOptions = computed(() => CONTRACT_ACTION_TYPE_OPTIONS);
  templateOptions = signal<Array<{ label: string; value: string; }>>([]);
  entityOptions = signal<AxaEntity[]>([]);
  currencyOptions = signal<FormDropdownItem[]>([]);
  
  // Template dialog
  showTemplateDialog = signal<boolean>(false);
  templateTableData = signal<any[]>([]);
  templateColumns: ColumnConfig[] = [
    { title: 'Nome Template', field: 'name', type: 'text', sortable: true, filterType: 'text' },
    { title: 'Descrizione', field: 'description', type: 'text', filterType: 'text' },
    { title: 'Azioni', type: 'action', filterType: 'none' }
  ];
  
  // Computed
  isEditMode = computed(() => !!this.actionId());
  pageTitle = computed(() => this.isEditMode() ? 'Modifica Azione Contrattuale' : 'Nuova Azione Contrattuale');
  canSave = computed(() => this.isFormValid() && !this.isSaving());
  
  selectedTemplate = computed(() => {
    const templateId = this.selectedTemplateId();
    if (!templateId) return null;
    const template = this.templateOptions().find(t => t.value === templateId);
    return template || null;
  });
  
  // Data minima per il datepicker (giorno dopo la data corrente del contratto)
  minEndDate = computed(() => {
    const current = this.currentEndDate();
    if (!current) return new Date(); // Se non c'è data corrente, usa oggi
    const minDate = new Date(current);
    minDate.setDate(minDate.getDate() + 1); // Almeno un giorno nel futuro
    return minDate;
  });

  constructor() {
    // Effect to pre-fill fields when action type changes in CREATE mode
    effect(() => {
      const type = this.actionType();
      
      // Only pre-fill in CREATE mode (not EDIT)
      if (this.isEditMode()) return;
      
      // Only pre-fill if we have loaded contract data
      if (!this.contractCode()) return;
      
      // Pre-fill based on selected action type
      if (type === 'MODIFY_SUBJECT') {
        this.newObject.set(this.currentObject());
      } else if (type === 'EXTEND_PARTIES') {
        this.newEntities.set([{ entityCode: '', signatoryName: '', signatoryRole: '' }]);
      }
      // Note: MODIFY_DURATION and MODIFY_ECONOMICS do NOT pre-fill - must be conscious choice
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    // Get route params
    this.route.params.subscribe(params => {
      console.log('Route params:', params);
      // Support both 'id' and 'contractId' params for backward compatibility
      const id = params['id'] || params['contractId'];
      const actionId = params['actionId'];
      
      console.log('Extracted id:', id, 'Type:', typeof id);
      console.log('Extracted actionId:', actionId);
      
      if (id) {
        // Store ID as string - can be numeric or alphanumeric (e.g., CNTR-000000001)
        this.contractId.set(id);
      }
      
      if (actionId) {
        this.actionId.set(+actionId);
      }
      
      // Setup initial breadcrumb (will be updated after loadData)
      this.setupBreadcrumb();
      this.updatePageTitle();
      this.loadData();
    });
  }

  private setupBreadcrumb(): void {
    // Use contractId from route first (it's what user navigated with),
    // fallback to contractCode from API, then to '...'
    const displayLabel = this.contractId() || this.contractCode() || '...';
    const contractsRegistryRoute = `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry`;
    
    const items: MenuItem[] = [
      {
        label: 'Registro Contratti',
        routerLink: contractsRegistryRoute
      },
      {
        label: `Contratto ${displayLabel}`,
        routerLink: `${contractsRegistryRoute}/${this.contractId()}`
      },
      {
        label: this.isEditMode() ? 'Modifica Azione' : 'Nuova Azione'
      }
    ];
    
    this.breadcrumbItems.set(items);
  }

  private updatePageTitle(): void {
    // Use contractId from route first (it's what user navigated with)
    const displayLabel = this.contractId() || this.contractCode() || '...';
    const title = this.isEditMode()
      ? `Modifica Azione - Contratto ${displayLabel}`
      : `Nuova Azione - Contratto ${displayLabel}`;
    this.titleService.setTitle(title);
  }

  private loadData(): void {
    this.isLoading.set(true);
    
    // Load contract info - contractId can be numeric or alphanumeric
    this.contractRegistryService.getContractById(this.contractId()).subscribe({
      next: (response) => {
        if (response && response.status && response.data) {
          const contract = response.data;
          
          // Save numeric ID from API response
          if (contract.id) {
            this.contractNumericId.set(contract.id);
          }
          
          this.contractName.set(contract.generalInfo?.contractSubject || 'Contratto');
          this.contractCode.set(contract.generalInfo?.contractInNumber || '');
          
          // Salva i valori correnti del contratto per validazione e pre-popolamento
          if (contract.generalInfo?.endDate) {
            this.currentEndDate.set(
              this.contractRegistryService.parseContractDate(contract.generalInfo.endDate),
            );
          }
          this.currentObject.set(contract.generalInfo?.contractSubject || '');
          this.currentFees.set(contract.economics?.totalContractValue || null);
          
          // Popola le entità correnti con involvedEntities dal contratto
          if (contract.generalInfo?.involvedEntities) {
            this.currentEntities.set(
              this.normalizeActionInvolvedEntities(contract.generalInfo.involvedEntities),
            );
          }
          
          // Update breadcrumb and title after loading contract data
          this.setupBreadcrumb();
          this.updatePageTitle();
          
          // Note: Pre-population is now handled by effect when actionType changes
          // This allows pre-filling fields when user selects different action types
        }
      },
      error: (err) => {
        console.error('Errore caricamento contratto:', err);
      }
    });
    
    // Load dropdowns
    this.loadDropdowns();
    
    // If edit mode, load action
    if (this.isEditMode()) {
      this.loadAction();
    } else {
      this.isLoading.set(false);
    }
  }

  private loadDropdowns(): void {
    // Load entity options
    this.axaEntityService.getAll().subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.entityOptions.set(response.data);
        }
      },
      error: (err) => {
        console.error('Errore caricamento entità:', err);
      }
    });

    // Load currency options
    this.formDropdownService.getFormDropdownValues('CURRENCY', 'CURRENCY').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.currencyOptions.set(response.data);
        }
      },
      error: (err) => {
        console.error('Errore caricamento valute:', err);
      }
    });
    
    // Load action template options from v2 DocumentAsset catalog
    // (MASTER_TEMPLATE con subDomainType=CONTRACTS). Il value è il businessKey.
    const domainType = this.navigationContext.isSupplierContext() ? 'SUPPLIERS' : 'PROFESSIONALS';
    this.documentTemplateV2Service
      .listMasterTemplates(domainType, 'CONTRACTS')
      .subscribe({
        next: (assets: DocumentAssetSummary[]) => {
          const selectable = assets.filter(isSelectableTemplate);
          const options = selectable.map((t) => ({
            label: t.name || t.businessKey,
            value: t.businessKey,
          }));
          this.templateOptions.set(options);
          this.templateTableData.set(selectable.map(toTemplateSelectionRow));

          console.log('✅ Template azioni caricati (v2):', {
            count: selectable.length,
            options,
            tableData: this.templateTableData(),
          });
        },
        error: (error) => {
          console.error('❌ Errore caricamento template (v2):', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare i template documento',
            life: 3000,
          });
        },
      });
  }
  
  // Template dialog methods
  openTemplateDialog(): void {
    this.showTemplateDialog.set(true);
  }

  closeTemplateDialog(): void {
    this.showTemplateDialog.set(false);
  }

  selectTemplate(template: any): void {
    // Il template passato può provenire da `templateTableData` (dove `id` è il
    // businessKey) oppure da un altro flusso. Preferiamo `businessKey` se
    // presente, altrimenti `id`.
    const templateData = {
      label: template.name,
      value: (template.businessKey ?? template.id) as string,
    };
    
    this.confirmDialogService.confirm({
      message: `Vuoi selezionare <strong>${escapeHtml(templateData.label)}</strong> come Template Documento?`,
      header: 'Conferma selezione',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Sì, seleziona',
      rejectLabel: 'Annulla',
      accept: () => {
        // Add to templateOptions if not present
        const currentOptions = this.templateOptions();
        const exists = currentOptions.some(opt => opt.value === templateData.value);
        if (!exists) {
          this.templateOptions.set([...currentOptions, templateData]);
        }
        
        this.selectedTemplateId.set(templateData.value);
        this.closeTemplateDialog();
        
        this.messageService.add({
          severity: 'success',
          summary: 'Template selezionato',
          detail: `${templateData.label} è stato selezionato come Template Documento`,
          life: 3000
        });
      }
    });
  }

  clearTemplate(): void {
    this.confirmDialogService.confirm({
      message: 'Vuoi rimuovere il template selezionato?',
      header: 'Conferma Rimozione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì',
      rejectLabel: 'No',
      accept: () => {
        this.selectedTemplateId.set(undefined);
        this.messageService.add({
          severity: 'info',
          summary: 'Template rimosso',
          detail: 'Template rimosso con successo',
          life: 3000
        });
      }
    });
  }

  private loadAction(): void {
    if (!this.actionId()) return;
    
    const contractId = this.contractId();
    
    this.contractActionService.getActionById(contractId, this.actionId()!).subscribe({
      next: (response: ApiResponse<ContractAction>) => {
        if (response.status && response.data) {
          this.action.set(response.data);
          this.populateForm(response.data);
          console.log('✅ Azione caricata:', response.data);
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('❌ Errore caricamento azione:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare l\'azione',
          life: 5000
        });
        this.isLoading.set(false);
      }
    });
  }

  private populateForm(action: ContractAction): void {
    this.actionType.set(action.actionType);
    this.actionName.set(action.actionName);
    this.actionNotes.set(action.notes || '');
    
    // Convert date if needed
    if (action.newEndDate) {
      this.newEndDate.set(
        this.contractRegistryService.parseContractDate(action.newEndDate) ?? undefined,
      );
    }
    
    this.newObject.set(action.newContractSubject || '');
    this.newCurrency.set(action.newCurrency);
    this.newTotalContractValue.set(action.newTotalContractValue);
    this.newAnnualValue.set(action.newAnnualValue);
    this.newEntities.set(
      this.normalizeActionInvolvedEntities(action.newInvolvedEntities).length > 0
        ? this.normalizeActionInvolvedEntities(action.newInvolvedEntities)
        : [{ entityCode: '', signatoryName: '', signatoryRole: '' }],
    );
    this.terminationReason.set(action.terminationReason || '');
    this.renewalNotes.set(action.renewalNotes || '');
    this.selectedTemplateId.set(action.selectedTemplateId);
  }

  private normalizeActionInvolvedEntities(
    value: ContractInvolvedEntity[] | string[] | null | undefined,
  ): ContractInvolvedEntity[] {
    if (!value || !Array.isArray(value)) {
      return [];
    }

    return value.map((item) => {
      if (typeof item === 'string') {
        return {
          entityCode: item,
          signatoryName: '',
          signatoryRole: '',
        };
      }

      return {
        entityCode: item?.entityCode || '',
        signatoryName: item?.signatoryName || '',
        signatoryRole: item?.signatoryRole || '',
      };
    });
  }

  private cloneActionInvolvedEntities(
    value: ContractInvolvedEntity[] | string[] | null | undefined,
  ): ContractInvolvedEntity[] {
    return this.normalizeActionInvolvedEntities(value).map((entity) => ({ ...entity }));
  }

  private getCurrentContractEntityCodes(): string[] {
    return this.currentEntities()
      .map((entity) => entity.entityCode?.trim())
      .filter((entityCode): entityCode is string => !!entityCode);
  }

  private getExtendableEntityOptions(): AxaEntity[] {
    const currentContractEntityCodes = new Set(this.getCurrentContractEntityCodes());
    return this.entityOptions().filter((option) => !currentContractEntityCodes.has(option.entityCode));
  }

  getAvailableInvolvedEntityOptions(currentIndex: number): AxaEntity[] {
    const selectedEntities = this.newEntities()
      .map((item, index) => (index === currentIndex ? null : item?.entityCode))
      .filter((value) => value !== null && value !== '');

    const currentValue = this.newEntities()[currentIndex]?.entityCode?.trim();

    return this.getExtendableEntityOptions().filter((option) => {
      if (currentValue && option.entityCode === currentValue) {
        return true;
      }

      return !selectedEntities.includes(option.entityCode);
    });
  }

  canAddMoreInvolvedEntities(): boolean {
    return this.newEntities().length < this.getExtendableEntityOptions().length;
  }

  addInvolvedEntity(): void {
    if (!this.canAddMoreInvolvedEntities()) {
      return;
    }

    this.newEntities.set([
      ...this.newEntities(),
      { entityCode: '', signatoryName: '', signatoryRole: '' },
    ]);
  }

  removeInvolvedEntity(index: number): void {
    if (this.newEntities().length <= 1) {
      return;
    }

    this.newEntities.set(this.newEntities().filter((_, itemIndex) => itemIndex !== index));
  }

  getInvolvedEntityLabel(entityCode: string | null | undefined): string {
    const option = this.entityOptions().find((item) => item.entityCode === entityCode);
    return option?.entityDescription || entityCode || '-';
  }

  onInvolvedEntityChange(index: number): void {
    const entities = this.newEntities();
    const selectedEntity = this.entityOptions().find(
      (entity) => entity.entityCode === entities[index]?.entityCode,
    );

    this.newEntities.set(
      entities.map((entity, entityIndex) =>
        entityIndex === index
          ? {
              ...entity,
              signatoryName: selectedEntity?.signatoryName || '',
              signatoryRole: selectedEntity?.signatoryRole || '',
            }
          : entity,
      ),
    );
  }

  hasValidExtendPartiesSelection(): boolean {
    if (this.newEntities().length === 0) {
      return false;
    }

    const entityCodes = this.newEntities()
      .map((entity) => entity.entityCode?.trim())
      .filter((entityCode): entityCode is string => !!entityCode);

    return entityCodes.length === this.newEntities().length
      && new Set(entityCodes).size === entityCodes.length;
  }

  isFormValid(): boolean {
    const type = this.actionType();
    const name = this.actionName().trim();
    
    if (!type || !name) {
      return false;
    }
    
    // Template validation: required for all action types except RENEW_CONTRACT and TERMINATION
    if (type !== 'RENEW_CONTRACT' && type !== 'TERMINATION') {
      if (!this.selectedTemplateId()) {
        return false;
      }
    }
    
    // Type-specific validation
    switch (type) {
      case 'MODIFY_DURATION':
        // Valida che ci sia una data E che sia nel futuro rispetto alla data corrente del contratto
        if (!this.newEndDate()) {
          return false;
        }
        const currentEnd = this.currentEndDate();
        if (currentEnd && this.newEndDate()! <= currentEnd) {
          return false; // La nuova data deve essere dopo la data corrente
        }
        return true;
      
      case 'MODIFY_SUBJECT':
        return !!this.newObject() && this.newObject().trim().length > 0;
      
      case 'MODIFY_ECONOMICS':
        // Validate that all three economic fields are filled
        return !!this.newCurrency() && 
               this.newTotalContractValue() !== undefined && 
               this.newTotalContractValue() !== null && 
               this.newTotalContractValue()! >= 0 &&
               this.newAnnualValue() !== undefined && 
               this.newAnnualValue() !== null && 
               this.newAnnualValue()! >= 0;
      
      case 'EXTEND_PARTIES':
        return this.hasValidExtendPartiesSelection();
      
      case 'TERMINATION':
        return !!this.terminationReason() && this.terminationReason().trim().length > 0;
      
      case 'RENEW_CONTRACT':
        return true;
      
      default:
        return false;
    }
  }

  onActionTypeChange(): void {
    // Auto-populate action name with decoded label
    const type = this.actionType();
    if (type) {
      const label = CONTRACT_ACTION_TYPE_LABELS[type];
      this.actionName.set(label || type);
    }
    
    // Reset type-specific fields when type changes
    this.newEndDate.set(undefined);
    this.newObject.set('');
    this.newCurrency.set(undefined);
    this.newTotalContractValue.set(undefined);
    this.newAnnualValue.set(undefined);
    this.newEntities.set(
      type === 'EXTEND_PARTIES'
        ? [{ entityCode: '', signatoryName: '', signatoryRole: '' }]
        : [],
    );
    this.terminationReason.set('');
    this.renewalNotes.set('');
    this.selectedTemplateId.set(undefined);
  }

  /**
   * Helper method to get numeric contract ID for API calls
   * Tries to use saved numeric ID, otherwise tries to parse from string
   */
  private getContractNumericId(): number {
    // Prefer the numeric ID saved from API response
    if (this.contractNumericId()) {
      return this.contractNumericId()!;
    }
    
    // Fallback: try to parse contractId as number
    const id = this.contractId();
    const parsed = parseInt(id, 10);
    
    if (!isNaN(parsed)) {
      return parsed;
    }
    
    // Last resort: throw error - should not happen if data loaded correctly
    throw new Error(`Cannot convert contractId '${id}' to numeric ID`);
  }

  /**
   * Valida i campi di testo per caratteri speciali non permessi (XSS protection)
   * @returns Array di nomi campi con caratteri speciali
   */
  private hasSpecialCharsInFields(): string[] {
    const fieldsWithErrors: string[] = [];
    
    // Pattern per strict mode (blocca < > " ' & / \)
    const strictPattern = /^[^<>"'&/\\]*$/;
    
    // Pattern per description mode (permette punteggiatura)
    const descriptionPattern = /^[a-zA-Z0-9\s\.,;:!?@#$%\^\-_()[\]{}+=\n\r]*$/;
    
    // Valida actionName (obbligatorio, strict mode)
    if (this.actionName() && !strictPattern.test(this.actionName())) {
      fieldsWithErrors.push('Nome Azione');
    }
    
    // Valida actionNotes (facoltativo, description mode)
    if (this.actionNotes() && !descriptionPattern.test(this.actionNotes())) {
      fieldsWithErrors.push('Descrizione');
    }
    
    // Valida campi specifici per tipo di azione
    const type = this.actionType();
    
    if (type === 'MODIFY_SUBJECT') {
      // newObject (obbligatorio, description mode)
      if (this.newObject() && !descriptionPattern.test(this.newObject())) {
        fieldsWithErrors.push('Nuovo Oggetto del Contratto');
      }
    }
    
    if (type === 'RENEW_CONTRACT') {
      // renewalNotes (facoltativo, description mode)
      if (this.renewalNotes() && !descriptionPattern.test(this.renewalNotes())) {
        fieldsWithErrors.push('Note sul Rinnovo');
      }
    }
    
    if (type === 'TERMINATION') {
      // terminationReason (obbligatorio, description mode)
      if (this.terminationReason() && !descriptionPattern.test(this.terminationReason())) {
        fieldsWithErrors.push('Motivazioni del Recesso');
      }
    }
    
    return fieldsWithErrors;
  }

  save(): void {
    if (!this.canSave()) return;
    
    // ✅ XSS VALIDATION: Controlla caratteri speciali PRIMA del salvataggio
    const specialCharsFields = this.hasSpecialCharsInFields();
    if (specialCharsFields.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }
    
    this.isSaving.set(true);
    
    const contractId = this.contractId();
    const type = this.actionType()!;
    
    // Build request according to API spec
    const request: CreateActionRequest = {
      actionType: type,
      actionName: this.actionName() || undefined,
      description: this.actionNotes() || undefined,
      selectedTemplateId: this.selectedTemplateId()
    };
    
    // Add type-specific fields
    switch (type) {
      case 'MODIFY_DURATION':
        if (this.newEndDate()) {
          request.newEndDate =
            this.contractRegistryService.formatContractDateForApi(this.newEndDate()) ?? undefined;
        }
        break;
      
      case 'MODIFY_SUBJECT':
        request.newContractSubject = this.newObject();
        break;
      
      case 'MODIFY_ECONOMICS':
        request.newCurrency = this.newCurrency();
        request.newTotalContractValue = this.newTotalContractValue();
        request.newAnnualValue = this.newAnnualValue();
        break;
      
      case 'EXTEND_PARTIES':
        request.newInvolvedEntities = this.newEntities().map((entity) => ({
          entityCode: entity.entityCode?.trim() || '',
          signatoryName: entity.signatoryName?.trim() || '',
          signatoryRole: entity.signatoryRole?.trim() || '',
        }));
        break;
      
      case 'TERMINATION':
        request.terminationReason = this.terminationReason();
        break;
      
      case 'RENEW_CONTRACT':
        // No additional fields for renewal
        break;
    }
    
    const serviceCall$ = this.isEditMode()
      ? this.contractActionService.updateAction(contractId, this.actionId()!, request)
      : this.contractActionService.createAction(contractId, request);
    
    serviceCall$.subscribe({
      next: (response: ApiResponse<ContractAction>) => {
        if (response.status && response.data) {
          this.messageService.add({
            severity: 'success',
            summary: this.isEditMode() ? 'Azione Aggiornata' : 'Azione Creata',
            detail: response.message || `L'azione è stata ${this.isEditMode() ? 'aggiornata' : 'creata'} con successo`,
            life: 3000
          });
          
          console.log('✅ Azione salvata:', response.data);
          
          // Navigate to action detail (for new actions) or back to contract detail (for edit)
          setTimeout(() => {
            const contractsRegistryRoute = `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry`;
            if (this.isEditMode()) {
              // For edit mode, go back to action detail
              this.router.navigate([`${contractsRegistryRoute}/${contractId}/actions/${this.actionId()}`]);
            } else {
              // For new action, go to action detail
              const newActionId = response.data?.id;
              if (newActionId) {
                this.router.navigate([`${contractsRegistryRoute}/${contractId}/actions/${newActionId}`]);
              } else {
                console.error('⚠️ ID azione non disponibile nella risposta');
                this.router.navigate([`${contractsRegistryRoute}/${contractId}`]);
              }
            }
          }, 1000);
        }
      },
      error: (err) => {
        console.error('❌ Errore salvataggio azione:', err);
        
        let errorMessage = `Errore nel ${this.isEditMode() ? 'aggiornamento' : 'creazione'} dell'azione`;
        
        if (err.error?.message) {
          errorMessage = err.error.message;
        } else if (err.error?.errors && err.error.errors.length > 0) {
          errorMessage = err.error.errors.map((e: any) => `${e.field}: ${e.message}`).join(', ');
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: errorMessage,
          life: 5000
        });
        this.isSaving.set(false);
      }
    });
  }

  cancel(): void {
    this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry/${this.contractId()}`], {
      fragment: 'actions'
    });
  }

  getActionTypeLabel(type: string): string {
    const label = CONTRACT_ACTION_TYPE_LABELS[type as keyof typeof CONTRACT_ACTION_TYPE_LABELS];
    if (!label) {
      console.warn('⚠️ Unknown action type:', type);
    }
    return label || type;
  }

  getActionStatusLabel(status: string): string {
    return CONTRACT_ACTION_STATUS_LABELS[status as keyof typeof CONTRACT_ACTION_STATUS_LABELS] || status;
  }
}
